import { useSearchParams } from 'react-router-dom';
import {
  BillingHeader,
  BillingEmptyRow,
  BillingPagination,
  BillingStatus
} from './components/OrganizationBillingLayout';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faDownload,
  faEye,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
  useGetOrganizationInvoicesQuery,
  useGetOrganizationInvoiceQuery,
  useGetOrganizationPaymentMethodsQuery
} from 'rtk/org-admin/organizationBilling.slice';
import {
  formatUsdCents,
  formatBillingDate
} from 'util/organizationBillingFormat';
import { downloadBillingInvoice } from 'util/organizationBillingDownload';
import OrganizationPayNowModal from './components/OrganizationPayNowModal';
import OrganizationInvoiceDetailModal from './components/OrganizationInvoiceDetailModal';

const dataOf = value => value?.Data ?? value?.data ?? value;
const EMPTY_INVOICES = [];
const invoiceTypeLabel = type =>
  ({
    Subscription: 'Subscription',
    HostedAccount: 'Hosted accounts',
    NoCharges: 'No charges',
    Mixed: 'Subscription + hosted accounts'
  }[type] ?? 'Other');
const errorText = error =>
  error?.data?.Data ??
  error?.data?.data ??
  error?.error ??
  'Unable to load organization invoices.';

const AdminInvoices = () => {
  const [searchParams] = useSearchParams();
  const linkedInvoice = searchParams.get('notificationEntity');
  const [status, setStatus] = useState('');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (linkedInvoice) setSelected(linkedInvoice);
  }, [linkedInvoice]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [paying, setPaying] = useState(null);
  const [verificationNotice, setVerificationNotice] = useState(false);
  const [settlementGuid, setSettlementGuid] = useState(null);
  const list = useGetOrganizationInvoicesQuery(
    {
      indexStart: page * pageSize,
      maxResults: pageSize,
      ...(status || search
        ? {
            filters: {
              ...(status ? { status } : {}),
              ...(search ? { search } : {})
            }
          }
        : {})
    },
    { pollingInterval: verificationNotice ? 3000 : 0 }
  );
  // The typed text runs ahead of the debounced query term; count that as loading.
  const listLoading = list.isFetching || searchText.trim() !== search;
  const detail = useGetOrganizationInvoiceQuery(selected, { skip: !selected });
  const cards = useGetOrganizationPaymentMethodsQuery();
  const payload = dataOf(list.data);
  const invoices = payload?.Objects ?? EMPTY_INVOICES;
  const detailData = dataOf(detail.data);
  const cardData = dataOf(cards.data);
  const cardList = Array.isArray(cardData) ? cardData : cardData?.Objects ?? [];

  useEffect(() => {
    if (
      verificationNotice &&
      invoices.some(
        invoice => invoice.GUID === settlementGuid && invoice.Status === 'Paid'
      )
    ) {
      setVerificationNotice(false);
    }
  }, [invoices, settlementGuid, verificationNotice]);

  const refreshSettlement = async result => {
    setSettlementGuid(paying?.GUID);
    setVerificationNotice(result?.IsSettled !== true);
    await list.refetch();
    if (selected) await detail.refetch();
  };

  const downloadPdf = async guid => {
    setDownloading(guid);
    setDownloadError('');
    try {
      await downloadBillingInvoice(guid);
    } catch (error) {
      setDownloadError(
        error?.message ?? 'Unable to download the invoice PDF. Retry shortly.'
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card>
      <BillingHeader
        title="Organization invoices"
        subtitle="View invoices, download receipts, and manage outstanding payments."
        onRefresh={() => list.refetch()}
        loading={list.isFetching}
      />
      <Card.Body className="px-4">
        <Row className="g-3 mb-3">
          <Col xs={12} md={6} lg={4}>
            <Form.Label htmlFor="invoice-search">Search invoices</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-white">
                <FontAwesomeIcon icon={faSearch} className="text-500" />
              </InputGroup.Text>
              <Form.Control
                id="invoice-search"
                type="search"
                placeholder="Invoice number"
                autoComplete="off"
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Form.Label htmlFor="invoice-status">Invoice status</Form.Label>
            <Form.Select
              id="invoice-status"
              value={status}
              onChange={event => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              <option value="">All invoices</option>
              <option value="Open">Open</option>
              <option value="Paid">Paid</option>
            </Form.Select>
          </Col>
        </Row>
        {downloadError && <Alert variant="danger">{downloadError}</Alert>}
        {verificationNotice && (
          <Alert variant="info">
            Checking payment status. The invoice status updates after the server
            confirms settlement.
          </Alert>
        )}
        {list.isLoading && (
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading invoices</span>
          </Spinner>
        )}
        {list.error && <Alert variant="danger">{errorText(list.error)}</Alert>}
        {cards.error && (
          <Alert variant="warning">
            Payment cards could not be loaded. Refresh before making a payment.
          </Alert>
        )}
        <BillingPagination
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(0);
          }}
          data={payload}
          onRefresh={() => list.refetch()}
          loading={list.isLoading || list.isFetching}
          error={list.error}
        />
        <div className="border rounded-3 overflow-hidden">
          <Table
            responsive
            hover
            className={`fs--1 mb-0 align-middle table-borderless${
              listLoading ? ' refreshing-skeleton' : ''
            }`}
          >
            <thead className="bg-200 text-900 text-nowrap align-middle">
              <tr>
                <th scope="col" className="ps-3">
                  Invoice
                </th>
                <th scope="col">Type</th>
                <th scope="col">Due date</th>
                <th scope="col" className="text-end">
                  Total
                </th>
                <th scope="col">Status</th>
                <th scope="col" className="text-end pe-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="border-top">
              {!list.isLoading && !list.error && !invoices?.length && (
                <BillingEmptyRow colSpan={6}>
                  {search
                    ? 'No invoices match your search.'
                    : 'No invoices for this billing period.'}
                </BillingEmptyRow>
              )}
              {invoices.map(invoice => (
                <tr key={invoice.GUID} className="border-bottom">
                  <td className="ps-3 fw-semi-bold text-900">
                    {invoice.InvoiceNumber}
                  </td>
                  <td className="text-600">
                    {invoiceTypeLabel(invoice.InvoiceType)}
                  </td>
                  <td>{formatBillingDate(invoice.DueDateUtc)}</td>
                  <td className="text-end fw-semi-bold text-900">
                    {formatUsdCents(invoice.TotalCents)}
                  </td>
                  <td>
                    <BillingStatus>{invoice.Status}</BillingStatus>
                  </td>
                  <td className="pe-3">
                    <div className="d-flex flex-wrap justify-content-end gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setSelected(invoice.GUID)}
                      >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        Details
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={Boolean(downloading)}
                        onClick={() => downloadPdf(invoice.GUID)}
                      >
                        {downloading === invoice.GUID ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                        ) : (
                          <FontAwesomeIcon icon={faDownload} className="me-1" />
                        )}
                        {downloading === invoice.GUID
                          ? 'Downloading...'
                          : 'Download PDF'}
                      </Button>
                      {invoice.Status === 'Open' && (
                        <Button
                          size="sm"
                          disabled={cards.isLoading || Boolean(cards.error)}
                          onClick={() => setPaying(invoice)}
                        >
                          <FontAwesomeIcon
                            icon={faCreditCard}
                            className="me-1"
                          />
                          Pay now
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <OrganizationInvoiceDetailModal
          show={Boolean(selected)}
          onHide={() => setSelected(null)}
          detail={detail}
          detailData={detailData}
          downloading={downloading}
          downloadError={downloadError}
          onDownloadPdf={downloadPdf}
          onPayNow={invoice => setPaying(invoice)}
          canPay={!cards.isLoading && !Boolean(cards.error)}
        />
        {paying && (
          <OrganizationPayNowModal
            show
            onHide={() => setPaying(null)}
            invoice={paying}
            cards={cardList}
            onSuccess={refreshSettlement}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default AdminInvoices;
